//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  ActiveVersion,
  HistoricTitleEntity,
  isActiveVersionAVersion,
  ModuleType,
  TITLE_HISTORY_DEFAULT_KEY,
  TitleEntity,
  TitleResponse,
  TitleVersionsResponse
} from '@/redux/slices/slice_types';
import { getJob } from '@/redux/slices/jobSlice';
import { parseFns } from '@/redux/selectors/configSelectors';
import { LoadingStatuses } from '@/constants/constants';
import axios from '@/_utils/axios';
import { normalizeTitleData, sortTitleChildren } from '@/_utils/titleUtils';
import { savePreferences } from './authSlice';

interface TitleSliceState {
  data: TitleEntity | null;
  seasons: Record<string, TitleEntity>;
  activeTitleId: number | null;
  activeTitleLevel: number | null;
  activeTitleHierarchy: number[];
  selectedTitleId: number | null;
  filteredChildId: number | null;
  activeVersion: ActiveVersion;
  activeVersions: TitleEntity[];
  titleDataById: Record<string, TitleEntity>;
  status: LoadingStatuses;
  versionStatus: LoadingStatuses;
}

const initialState: TitleSliceState = {
  data: null,
  seasons: {},
  activeTitleId: null,
  activeTitleLevel: null,
  activeTitleHierarchy: [],
  selectedTitleId: null,
  filteredChildId: null,
  activeVersion: null,
  activeVersions: [],
  titleDataById: {},
  status: LoadingStatuses.fulfilled,
  versionStatus: LoadingStatuses.fulfilled
};

// THUNKS
export const getTitleDetails = createAsyncThunk('title/getTitleDetails', async (id: number, { rejectWithValue, signal, dispatch }) => {
  return axios
    .get<TitleResponse>(`/inventory/titles/${id}/`, { signal })
    .then((titleRes) => {
      const titleData = titleRes.data?.data || {};
      const seasons: Record<string, TitleEntity> = {};
      if (['series', 'campaign', 'compilation'].includes(titleRes.data?.data?.entity_type)) {
        titleData.children?.forEach((season) => {
          seasons[season.id] = season;
          seasons[season.id].children = seasons[season.id].children.map((ep) => ({
            ...ep,
            season_id: season.id
          }));
        });
      }

      dispatch(saveTitleToHistory(titleData));

      return { titleData, seasons };
    })
    .catch((error) => rejectWithValue(error.data));
});

export const saveTitleToHistory = createAsyncThunk('title/saveTitleToHistory', async (titleData: HistoricTitleEntity, thunkAPI) => {
  const { getState, dispatch } = thunkAPI;
  const state = getState() as RootState;
  const currentModule = state.dataTable.currentModule;

  try {
    if (titleData.id && titleData.name && currentModule) {
      const history = state.auth.preferences?.titles?.history || {};
      const oldModuleHistory = history[currentModule] || {};

      const bucketKey = titleData.hierarchy ?? TITLE_HISTORY_DEFAULT_KEY;
      const oldBucketHistory = oldModuleHistory[bucketKey] || [];

      // Keep only last 5 per bucket
      const filteredBucketHistory = oldBucketHistory.filter((item: HistoricTitleEntity) => item?.id !== titleData.id);

      const newBucketHistory = [
        ...filteredBucketHistory.slice(-4),
        {
          id: titleData.id,
          name: titleData.name,
          ...(titleData.hierarchy && { hierarchy: titleData.hierarchy })
        }
      ];

      // keep only valid modules
      const validModuleTypes: ModuleType[] = ['orderPlacement', 'packaging'];
      const filteredExistingHistory = Object.keys(history).reduce(
        (acc, key) => {
          if (validModuleTypes.includes(key as ModuleType)) {
            acc[key as ModuleType] = history[key];
          }
          return acc;
        },
        {} as Record<ModuleType, Record<string, HistoricTitleEntity[]>>
      );

      dispatch(
        savePreferences({
          titles: {
            history: {
              ...filteredExistingHistory,
              [currentModule]: {
                ...oldModuleHistory,
                [bucketKey]: newBucketHistory
              }
            }
          }
        })
      );
    }
  } catch (err) {
    console.warn('History update failed:', err);
  }

  return titleData;
});

export const getTitleVersions = createAsyncThunk('title/getTitleVersions', async (id: number, { rejectWithValue, getState }) => {
  const parseVersionsFn = parseFns.orderPlacementConfig?.parseVersionsFn;
  const state = getState() as RootState;
  const titleSearchValue = state.orderPlacement.searchValue;

  return axios
    .post<TitleVersionsResponse>(`/inventory/titles/${id}/versions/?page_size=999`)
    .then(async (versionRes) => {
      if (versionRes.data?.pagination?.total > 999) {
        const versionRes2 = await axios.post<TitleVersionsResponse>(`/inventory/titles/${id}/versions/?page_size=999&page_num=1`);
        versionRes.data.data = [...versionRes.data.data, ...versionRes2.data.data];
      }
      let versions = versionRes.data?.data?.filter((version) => version.status === 'active') || [];
      versions = parseVersionsFn ? parseVersionsFn(versions, { titleSearchValue }) : versions;
      return { versions };
    })
    .catch((error) => rejectWithValue(error.data));
});

const titleSlice = createSlice({
  name: 'title',
  initialState,
  reducers: {
    clearTitle: (state) => {
      state.data = initialState.data;
      state.seasons = initialState.seasons;
      state.activeTitleId = initialState.activeTitleId;
      state.activeTitleLevel = initialState.activeTitleLevel;
      state.activeTitleHierarchy = initialState.activeTitleHierarchy;
      state.selectedTitleId = initialState.selectedTitleId;
      state.activeVersion = initialState.activeVersion;
      state.filteredChildId = initialState.filteredChildId;
    },
    resumeTitleSliceDraft: (state, { payload }) => {
      state.data = payload.activeTitle;
      state.titleDataById = payload.titleDataById;
      state.activeTitleId = payload.activeTitleId;
    },
    setActiveTitle: (state, { payload }: { payload: number | null }) => {
      state.activeTitleId = payload;
    },
    setSelectedTitleId: (state, { payload }: { payload: number | null }) => {
      state.selectedTitleId = payload;
    },
    setActiveTitleLevel: (state, { payload }: { payload: number | null }) => {
      state.activeTitleLevel = payload;
    },
    updateActiveTitleHierarchy: (state, { payload }: { payload: { id: number; level: number } }) => {
      if (state.activeTitleHierarchy.length === 0) {
        state.activeTitleHierarchy = [state.data?.id as number, payload.id];
        return;
      }

      if (payload.level === state.activeTitleHierarchy.length) {
        state.activeTitleHierarchy[payload.level - 1] = payload.id;
      } else if (payload.level > state.activeTitleHierarchy.length) {
        state.activeTitleHierarchy.push(payload.id);
      } else {
        state.activeTitleHierarchy = state.activeTitleHierarchy.slice(0, payload.level - 1);
        state.activeTitleHierarchy.push(payload.id);
      }
    },
    setFilteredChildId: (state, { payload }: { payload: number | null }) => {
      state.filteredChildId = payload;
    },
    setActiveVersion: (state, { payload }: { payload: ActiveVersion }) => {
      state.activeVersion = payload;
    },
    setTitleDataById: (state, { payload: { id, data } }) => {
      state.titleDataById = {
        ...state.titleDataById,
        [id]: data
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getTitleDetails.pending, (state) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(getTitleDetails.rejected, (state) => {
      state.status = LoadingStatuses.rejected;
      state.activeTitleId = null;
    });
    builder.addCase(getTitleDetails.fulfilled, (state, { payload: { titleData, seasons } }) => {
      const parseDataFn = parseFns.titleConfig?.parseDataFn;
      const sortedTitleData = sortTitleChildren(titleData);
      state.data = parseDataFn ? parseDataFn(sortedTitleData) : sortedTitleData;
      state.status = LoadingStatuses.fulfilled;
      state.titleDataById = normalizeTitleData(sortedTitleData);

      // only used for OPv1 currently -- will phase out
      state.seasons = seasons;
    });
    builder.addCase(getTitleVersions.pending, (state) => {
      state.versionStatus = LoadingStatuses.pending;
    });
    builder.addCase(getTitleVersions.rejected, (state) => {
      state.versionStatus = LoadingStatuses.rejected;
    });
    builder.addCase(getTitleVersions.fulfilled, (state, { payload: { versions } }) => {
      state.versionStatus = LoadingStatuses.fulfilled;
      state.activeVersions = versions;
      if (isActiveVersionAVersion(state.activeVersion)) {
        const activeVersionId = state.activeVersion.id;
        state.activeVersion = versions.find((v) => v.id === activeVersionId) || null;
      }
    });
    // TODO: Title slice should not rely on the `getJob` thunk. The correct way to do it:
    //  whatever component calls `getJob`, when server returns the response, should dispatch setActiveVersion action.
    builder.addCase(getJob.fulfilled, (state, { payload: { data } }) => {
      state.activeVersion = data.version;
    });
  }
});

export const {
  clearTitle,
  setActiveTitle,
  setSelectedTitleId,
  setActiveTitleLevel,
  updateActiveTitleHierarchy,
  setFilteredChildId,
  setActiveVersion,
  resumeTitleSliceDraft,
  setTitleDataById
} = titleSlice.actions;

export default titleSlice.reducer;
